import { useState } from "react";

import { PhoneIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import PartProMessageChain from "../partProMessaging/PartProMessageChain";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IPartProConversation } from "../../models/PartProMessage";
import { IPartRequest, IPartRequestOffer } from "../../models/partRequests/IPartRequest";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";

interface NetworkOffersProps {
  request: IPartRequest;
  /** Messaging goes through PartPro, so the button is hidden when the yard is disconnected. */
  partProConnected: boolean;
}

// On our own network request we are the buyer side of the thread the other yard opened, so the
// chain flips its highlight and header via role/counterpartName.
function conversationForOffer(request: IPartRequest, offer: IPartRequestOffer): IPartProConversation {
  return {
    id: offer.conversationId ?? "",
    subject: `Network request: ${request.partWanted}`,
    buyerName: null,
    role: "BUYER",
    counterpartName: offer.vendorName,
    listingId: offer.listingId,
    listingTitle: offer.listingTitle,
    listingTagNumber: offer.listingTagNumber,
    orderId: null,
    orderNumber: null,
    unread: false,
    lastMessageBody: null,
    lastMessageSender: null,
    lastMessageAt: offer.offeredAt,
    partId: null,
    partTagNumber: null,
    partImageUrl: null,
    vehicleId: null,
    vehicleVrm: null,
    vehicleDescription: null,
  };
}

export default function NetworkOffers({ request, partProConnected }: NetworkOffersProps) {
  const [chainOpen, setChainOpen] = useState(false);
  const [chosen, setChosen] = useState<IPartProConversation | undefined>();

  if (!request.isOnNetwork && request.offers.length === 0) return null;

  return (
    <div className="mt-6">
      <PartProMessageChain open={chainOpen} setOpen={setChainOpen} conversation={chosen} />

      <h2 className="text-lg font-semibold text-gray-900">Offers from the network</h2>
      <p className="text-sm text-gray-500">
        Yards that have confirmed they can supply this part. Buy it from them and sell it on to your customer.
      </p>

      {request.offers.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">
          No offers yet. Other yards are notified as soon as a request goes out; offers appear here within a few minutes of being made.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200">
          {request.offers.map((offer) => (
            <li key={offer.id} className="flex flex-wrap items-start justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-medium text-gray-900">
                  {offer.vendorName ?? "Yard"}
                  {offer.vendorCity && <span className="text-gray-500"> · {offer.vendorCity}</span>}
                </p>
                <p className="text-xs text-gray-500">
                  Offered {formatDateTime(offer.offeredAt, DateTime.DATETIME_SHORT)}
                </p>
                {offer.message && <p className="mt-2 whitespace-pre-line text-sm text-gray-700">{offer.message}</p>}
                {offer.listingTitle && (
                  <button
                    type="button"
                    onClick={() => offer.listingUrl && openInNewTab(offer.listingUrl)}
                    className="mt-2 text-sm text-primary-600 hover:underline disabled:text-gray-500"
                    disabled={!offer.listingUrl}
                  >
                    {offer.listingTitle}
                    {offer.listingTagNumber ? ` · Tag ${offer.listingTagNumber}` : ""}
                  </button>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {offer.vendorPhone && (
                  <a
                    href={`tel:${offer.vendorPhone}`}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <PhoneIcon className="h-4 w-4" /> {offer.vendorPhone}
                  </a>
                )}
                {offer.conversationId && partProConnected && (
                  <ButtonInput
                    label="Message yard"
                    classes=""
                    onClick={() => {
                      setChosen(conversationForOffer(request, offer));
                      setChainOpen(true);
                    }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
